
// This is a utility for making OpenAI API calls
// It uses the Supabase Edge Function to securely access the OpenAI API

import { supabase } from "@/integrations/supabase/client";
import { AssistantData } from "@/types/assistant";

export async function getOpenAIResponse(
  messages: { role: string; content: string }[],
  assistantData?: AssistantData | null
) {
  try {
    // Log the assistant data being used
    console.log("Sending request to OpenAI with assistant data:", assistantData ? {
      id: assistantData.id,
      hotelId: assistantData.hotel_id,
      urlSlug: assistantData.url_slug
    } : "No assistant data, using defaults");
    
    // Call our Supabase Edge Function that securely uses OpenAI
    const response = await supabase.functions.invoke('chat-with-openai', {
      body: { 
        messages,
        assistantData // Pass the entire assistantData object
      },
    });

    // Get the status code from the response object
    const statusCode = response.error ? 500 : 200;
    console.log("Edge function response status:", statusCode);
    
    if (response.error) {
      console.error('Error calling OpenAI via Edge Function:', response.error);
      
      // Provide more detailed debugging information
      const errorDetails = response.error.message || JSON.stringify(response.error);
      console.log("Error details from edge function:", errorDetails);
      
      // Check for connection issues with Supabase
      if (response.error.message?.includes('fetch failed') || 
          response.error.message?.includes('network') || 
          response.error.message?.includes('non-2xx status code')) {
        return {
          message: "We're having trouble connecting to our backend services. Please check your internet connection or try again later.",
          cached: false,
          systemPrompt: "Connection error",
          fullPromptContext: [
            { role: "system", content: "Connection error" },
            ...messages
          ],
          error: true,
          errorType: "supabase_connection_error",
          errorDetails: errorDetails,
          statusCode: statusCode
        };
      }
      
      return {
        message: "There was an error processing your request. Please try again later.",
        cached: false,
        systemPrompt: "Error occurred",
        fullPromptContext: [
          { role: "system", content: "Error occurred" },
          ...messages
        ],
        error: true,
        errorType: "technical_error",
        errorDetails: errorDetails,
        statusCode: statusCode
      };
    }

    // Check if the response contains an error field
    if (response.data && response.data.error) {
      console.error("OpenAI API error response:", response.data.error);
      
      // Create a friendly error response based on the error type
      let errorMessage = response.data.message || "I'm sorry, but I couldn't process your request. Please try again later.";
      let errorType = response.data.error;
      let errorDetails = response.data.errorDetails || response.data.error;
      
      if (response.data.error === "invalid_api_key") {
        errorDetails = "OpenAI API key is invalid or not configured correctly. Please check your API key configuration.";
        errorMessage = "The AI service is not properly configured. Please check your OpenAI API key in the Supabase Edge Function secrets.";
      }
      
      // If the response includes the systemPrompt and fullPromptContext, include them in the error response
      return {
        message: errorMessage,
        cached: false,
        systemPrompt: response.data.systemPrompt || "Error occurred",
        fullPromptContext: response.data.fullPromptContext || [
          { role: "system", content: "Error occurred" },
          ...messages
        ],
        error: true,
        errorType: errorType,
        errorDetails: errorDetails,
        statusCode: statusCode,
        rawResponse: response.data
      };
    }

    return {
      ...response.data,
      cached: false,  // Explicitly mark this as not cached since it came from API
      statusCode: statusCode
    };
  } catch (error) {
    console.error('Error getting response:', error);
    
    let errorMessage = "I'm sorry, but I couldn't process your request. Please try again later.";
    let errorType = "technical_error";
    let errorDetails = "";
    let statusCode = 500;
    
    if (error instanceof Error) {
      errorDetails = `${error.name}: ${error.message}\n${error.stack || ""}`;
      
      // Try to extract status code if it's in the error message
      const statusCodeMatch = error.message.match(/status code (\d+)/i);
      if (statusCodeMatch && statusCodeMatch[1]) {
        statusCode = parseInt(statusCodeMatch[1], 10);
      }
    } else {
      errorDetails = String(error);
    }
    
    // Handle specific error messages
    if (error.message === "quota_exceeded" || 
        error.message?.includes("insufficient_quota") || 
        error.message?.includes("quota")) {
      errorMessage = "I apologize, but our AI service is temporarily unavailable due to usage limits. We'll be back online soon!";
      errorType = "quota_exceeded";
    } else if (error.message?.includes("invalid_api_key") || error.message?.includes("API key")) {
      errorMessage = "The AI service is not properly configured. Please contact an administrator to verify the API key.";
      errorType = "invalid_api_key";
    } else if (error.message?.includes("fetch failed") || 
               error.message?.includes("network") ||
               error.message?.includes("connection")) {
      errorMessage = "We're having trouble connecting to our backend services. Please check your internet connection or try again later.";
      errorType = "connection_error";
    } else if (error.message?.includes("non-2xx status code")) {
      errorMessage = "The OpenAI service returned an error. This is often due to an invalid API key or exceeded quota.";
      errorType = "api_error";
    }
    
    // Create an error message that's more user-friendly
    return {
      message: errorMessage,
      cached: false,
      systemPrompt: "Error message",
      fullPromptContext: [
        { role: "system", content: "Error message" },
        ...messages
      ],
      error: true,
      errorType: errorType,
      errorDetails: errorDetails,
      statusCode: statusCode
    };
  }
}
